import optimizedArtwork from "../../assets/images/progressive-bonuses-base.webp";
import {
  formatBonusAmount,
  mainBonusFields,
  progressiveBonusOverlayLayout,
  progressiveBonusDefaults,
  straightFlushFields,
} from "./progressiveBonusConfig";

export const PROGRESSIVE_BONUS_IMAGE_WIDTH = 1536;
export const PROGRESSIVE_BONUS_IMAGE_HEIGHT = 1024;
export const PROGRESSIVE_BONUS_IMAGE_FILENAME = "the-royal-flush-progressive-bonuses.png";

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load the progressive bonus artwork."));
    image.src = source;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Unable to create the progressive bonus image."));
      }
    }, "image/png");
  });
}

function drawText(context, text, layout, options) {
  const fontSize = Number((PROGRESSIVE_BONUS_IMAGE_WIDTH * layout.fontSize / 100).toFixed(3));

  context.save();
  context.fillStyle = options.color;
  context.font = `900 ${fontSize}px Georgia, "Times New Roman", serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.shadowColor = options.shadowColor;
  context.shadowBlur = options.shadowBlur;
  context.fillText(
    text,
    PROGRESSIVE_BONUS_IMAGE_WIDTH * layout.left / 100,
    PROGRESSIVE_BONUS_IMAGE_HEIGHT * layout.top / 100
  );
  context.restore();
}

export function drawProgressiveBonusesImage(context, artwork, bonuses) {
  const currentBonuses = { ...progressiveBonusDefaults, ...bonuses };

  context.drawImage(
    artwork,
    0,
    0,
    PROGRESSIVE_BONUS_IMAGE_WIDTH,
    PROGRESSIVE_BONUS_IMAGE_HEIGHT
  );

  mainBonusFields.forEach(({ field }) => {
    drawText(context, formatBonusAmount(currentBonuses[field]), progressiveBonusOverlayLayout[field], {
      color: "#050505",
      shadowColor: "rgba(255, 255, 255, 0.25)",
      shadowBlur: 1,
    });
  });

  straightFlushFields.forEach(({ field }) => {
    drawText(context, formatBonusAmount(currentBonuses[field]), progressiveBonusOverlayLayout[field], {
      color: "#f7c936",
      shadowColor: "rgba(255, 201, 54, 0.35)",
      shadowBlur: 4,
    });
  });

  const rankLayout = progressiveBonusOverlayLayout.quad_of_day_rank;
  drawText(context, currentBonuses.quad_of_day_rank, {
    ...rankLayout,
    fontSize: currentBonuses.quad_of_day_rank === "TBD"
      ? rankLayout.pendingFontSize
      : rankLayout.fontSize,
  }, {
    color: "#f7c936",
    shadowColor: "rgba(255, 201, 54, 0.32)",
    shadowBlur: 11,
  });
}

export async function downloadProgressiveBonusesImage(bonuses) {
  const artwork = await loadImage(optimizedArtwork);
  const canvas = document.createElement("canvas");
  canvas.width = PROGRESSIVE_BONUS_IMAGE_WIDTH;
  canvas.height = PROGRESSIVE_BONUS_IMAGE_HEIGHT;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Image downloads are not supported in this browser.");
  }

  drawProgressiveBonusesImage(context, artwork, bonuses);
  const blob = await canvasToBlob(canvas);
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = downloadUrl;
  link.download = PROGRESSIVE_BONUS_IMAGE_FILENAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(downloadUrl);
}
